import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DoNotDisturb, InfoOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableV2, TableV2Column } from 'components/Table/TableV2';
import { TableScrollAffordances } from './TableScrollAffordances';
import { findScroller } from '../hooks/useTableHorizontalScroll';
import { scrollHorizontallyByKey } from 'common/table/utils/scrollHorizontallyByKey';
import { SearchInput } from 'common/ui/SearchInput';
import { AlertCard } from 'common/ui/AlertCard';
import { Button } from 'system/atoms';
import { SortOrder } from 'react-data-table-component';
import {
  SearchableConversationFilterInput,
  SearchableConversationSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { useSearchCrossCampaignConversationsQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useTableSort, useSearchablePagination } from 'common/hooks';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useTracking } from 'services/tracking/hooks';
import {
  Paths,
  deleteSearchParams,
  updateSearchParams
} from 'services/navigation/paths';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import {
  handleConversationFilterTracking,
  handleConversationSortTracking
} from 'modules/conversations/utils';
import { getCrossCampaignConversationColumns } from 'modules/conversations/utils/getCrossCampaignConversationColumns';
import { getConversationFilterLabel } from 'modules/conversations/utils/getConversationFilterLabel';
import { useCrossCampaignConversationsFilters } from 'modules/conversations/hooks/useCrossCampaignConversationsFilters';
import { ConversationVisibility } from 'modules/conversations/utils/buildConversationVisibilityClause';
import {
  useConversationNavigation,
  useConversationTagFilters
} from 'modules/conversations/hooks';
import { CrossCampaignConversationItem } from 'modules/conversations/types';
import { ConversationFilterIds } from 'modules/conversations/constants/enums';
import { crossCampaignConversationsSx } from 'modules/conversations/styles';
import { isTagFilterId } from 'modules/conversations/utils/tagFilterIds';
import { ConversationTagsFilterButton } from './ConversationTagsFilterButton';

// TableV2's own empty state is shared by every table; this tab has the design's.
const EmptyState: React.FC<{ isFiltering: boolean }> = ({ isFiltering }) => (
  <Box
    sx={
      isFiltering
        ? crossCampaignConversationsSx.emptyStateFiltering
        : crossCampaignConversationsSx.emptyStateIdle
    }
  >
    <Box sx={crossCampaignConversationsSx.emptyStateIcon(isFiltering)}>
      {isFiltering ? <InfoOutlined /> : <DoNotDisturb />}
    </Box>
    <Typography sx={crossCampaignConversationsSx.emptyStateTitle(isFiltering)}>
      Nothing {isFiltering ? 'found' : 'to show'}
    </Typography>
    {isFiltering && (
      <Typography sx={crossCampaignConversationsSx.emptyStateBody}>
        Search for something else or adjust your filters
      </Typography>
    )}
  </Box>
);

type Props = {
  organizationId: string;
  // Undefined for admins, who see the whole sub-account.
  visibility?: ConversationVisibility;
  // False while `visibility` is still being assembled.
  isVisibilityReady?: boolean;
  // True when it could not be assembled at all
  visibilityError?: boolean;
  onRetryVisibility?: () => void;
  // Reports the filter Export data should send: undefined while nothing is
  // applied. The button lives in the page's tab bar, not in this tab.
  onExportFilterChange?: (
    filter: SearchableConversationFilterInput | undefined
  ) => void;
};

/**
 * Conversations across every campaign the user can see in the current
 * sub-account. No live updates: the conversation subscriptions are
 * campaign-scoped, so this list is fetch-on-demand.
 */
export const CrossCampaignConversationsTab = ({
  organizationId,
  visibility,
  isVisibilityReady = true,
  visibilityError = false,
  onRetryVisibility,
  onExportFilterChange
}: Props) => {
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(50);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { track } = useTracking();
  const { openDrawer } = useDrawer();

  const { selectedTags, toggleTag, clearTag, clearTags, tagFilters } =
    useConversationTagFilters();

  const {
    queryFilter,
    exportFilter,
    isFiltering,
    appliedFilters,
    setConversationsTableFilters
  } = useCrossCampaignConversationsFilters({
    organizationId,
    searchText,
    visibility,
    tagFilters
  });

  // Keyed by content: the filter is rebuilt on every render.
  const exportFilterKey = exportFilter ? JSON.stringify(exportFilter) : '';

  useEffect(() => {
    onExportFilterChange?.(exportFilter);
    // Leaving the tab takes its filter with it.
    return () => onExportFilterChange?.(undefined);
  }, [exportFilterKey]);

  // After the filters, so the offset can key off the query and any filter change
  // returns to page one.
  const { from, currentPage, handleNext, handlePrevious } =
    useSearchablePagination({
      limit,
      resetKey: JSON.stringify(queryFilter)
    });

  const { sort, handleSort } = useTableSort<
    SearchableConversationSortableFields,
    CrossCampaignConversationItem
  >({
    field: SearchableConversationSortableFields.UpdatedAt,
    direction: SearchableSortDirection.Desc
  });

  const { data, loading } = useSearchCrossCampaignConversationsQuery({
    variables: {
      from,
      sort,
      limit,
      filter: queryFilter
    },
    skip: !isVisibilityReady
  });

  const conversations = useMemo(() => extractQueryListItems(data), [data]);
  const total = data?.searchConversations?.total ?? 0;

  const {
    total: navigationTotal,
    positionOf,
    moveToConversation,
    updateGlobalIndex
  } = useConversationNavigation({
    filter: queryFilter,
    sort,
    conversationIds: conversations.map((conversation) => conversation.id),
    total,
    pageFrom: from
  });

  const openConversationDetailsDrawer = (conversationId: string) => {
    const conversation = conversations.find(
      (item) => item.id === conversationId
    );
    const campaignTitle = conversation?.campaign?.title ?? '';

    const computedIndex = positionOf(conversationId);

    updateGlobalIndex(conversationId);

    updateSearchParams({
      searchParams,
      newParam: {
        key: 'conversationId',
        value: conversationId
      },
      setSearchParams
    });

    openDrawer({
      drawerContentType: DrawerComponentType.ConversationDetails,
      onClose: () => {
        deleteSearchParams({
          searchParams,
          key: 'conversationId',
          setSearchParams
        });
      },
      drawerComponentProps: {
        conversationId,
        jobTitle: campaignTitle,
        location: '',
        moveToConversation,
        conversationsCount: navigationTotal,
        conversationIndex: computedIndex
      }
    });
  };

  const conversationIdParam = searchParams.get('conversationId');

  useEffect(() => {
    // Deep link support: shared conversation URLs open the drawer directly.
    if (conversationIdParam) {
      openConversationDetailsDrawer(conversationIdParam);
    }
  }, [conversationIdParam, navigationTotal]);

  const onFilterSelect = (filter: {
    value: string | boolean;
    filterId?: string;
  }) => {
    const { filterId: filterIdString, value } = filter;
    const filterId = filterIdString as ConversationFilterIds;

    if (!filterId) {
      return;
    }

    setConversationsTableFilters((prev) => [
      ...(prev?.filter((item) => item.filterId !== filterId) ?? []),
      { filterId, value, label: getConversationFilterLabel(filterId, value) }
    ]);

    handleConversationFilterTracking({ filterId, track, value });
  };

  const handleOnSort = (
    column: TableV2Column<CrossCampaignConversationItem>,
    sortDirection: SortOrder
  ) => {
    handleSort(column, sortDirection);
    if (column.sortField) {
      handleConversationSortTracking({ track, sortField: column.sortField });
    }
  };

  const columns = useMemo(
    () =>
      getCrossCampaignConversationColumns({
        openConversationDetailsDrawer,
        onOpenCampaign: (campaignId) =>
          navigate(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`),
        tagsFilterComponent: (
          <ConversationTagsFilterButton
            organizationId={organizationId}
            selectedTags={selectedTags}
            onToggleTag={toggleTag}
            onClearTags={clearTags}
          />
        ),
        onTagClick: toggleTag,
        selectedTags,
        sort
      }),
    [organizationId, selectedTags, sort]
  );

  const minTableWidth = useMemo(
    () =>
      columns.reduce(
        (sum, { width, minWidth }) =>
          sum + parseInt(width ?? minWidth ?? '0', 10),
        0
      ),
    [columns]
  );

  const isLoadingList = loading || !isVisibilityReady;
  const isEmpty = !isLoadingList && conversations.length === 0;

  const tableAreaRef = useRef<HTMLDivElement>(null);

  // Not a spinner, since waiting will not produce a list; not a list either,
  // since it would silently omit every shared campaign.
  if (visibilityError) {
    return (
      <Box sx={crossCampaignConversationsSx.errorState}>
        <AlertCard
          type="error"
          title="Couldn't load the campaigns shared with you"
        >
          Your conversations have not been loaded, because the list would have
          left out every campaign shared with you.
        </AlertCard>
        {onRetryVisibility && (
          <Button variant="tertiary" onClick={onRetryVisibility}>
            Try again
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={crossCampaignConversationsSx.container}>
      <Box sx={crossCampaignConversationsSx.toolbar}>
        <SearchInput
          sx={crossCampaignConversationsSx.searchInput}
          placeholder="Search candidate name or ID"
          onChange={setSearchText}
        />
        <Typography sx={crossCampaignConversationsSx.countText}>
          {total} {total === 1 ? 'conversation' : 'conversations'}
        </Typography>
      </Box>

      <Box
        ref={tableAreaRef}
        sx={crossCampaignConversationsSx.table(sort.field)}
        tabIndex={0}
        role="region"
        aria-label="Conversations"
        onKeyDown={(event) =>
          scrollHorizontallyByKey(event, findScroller(tableAreaRef.current))
        }
      >
        <TableV2
          alwaysShowScrollbar
          minTableWidth={minTableWidth}
          columns={columns}
          data={conversations}
          pagination
          paginationServer
          paginationTotalRows={total}
          isLoading={isLoadingList}
          persistTableHead={!isEmpty}
          noDataComponent={<EmptyState isFiltering={isFiltering} />}
          sortField={sort.field}
          sortServer
          onSort={handleOnSort}
          isFiltering={isFiltering}
          onFilterSelect={onFilterSelect}
          serverPage={currentPage}
          paginationResetDefaultPage={false}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          onChangeRowsPerPage={(newPerPage) => setLimit(newPerPage)}
          onRemoveFilters={(filterId) => {
            if (isTagFilterId(filterId)) {
              clearTag(filterId);
              return;
            }

            setConversationsTableFilters((prev) =>
              (prev ?? []).filter((item) => item.filterId !== filterId)
            );
          }}
          filters={appliedFilters}
          onRemoveAllFilters={() => {
            setConversationsTableFilters([]);
            clearTags();
          }}
        />
        <TableScrollAffordances
          tableAreaRef={tableAreaRef}
          rerenderKey={`${conversations.length}-${isEmpty}`}
        />
        {isEmpty && (
          <Typography sx={crossCampaignConversationsSx.noResults}>
            No results
          </Typography>
        )}
      </Box>
    </Box>
  );
};
